/**
 * MarkdownTable - shared GFM table renderer for every Markdown render mode.
 *
 * Why this exists: each mode used to render `<table className="min-w-full">`
 * with the browser default `table-layout: auto`. `min-w-full` only sets a
 * *minimum* width, so a wide table was squeezed into whatever container it
 * landed in instead of scrolling: every column wrapped mid-word, numbers broke
 * across lines, and inside a narrow popover/overlay the result reads as a
 * broken layout. GFM column alignment (`|---:|`) was dropped too, because the
 * `th`/`td` renderers destructured only `children` and threw away the `style`
 * prop that remark-gfm puts `text-align` into.
 *
 * This renders the same visual language as MarkdownDatatableBlock - the app's
 * reference table - so a plain markdown table and a `datatable` block look like
 * the same component:
 *   - `w-max min-w-full`: the table grows to its content and the wrapper
 *     scrolls horizontally, so columns are never crushed.
 *   - Short cells stay on one line; only genuinely long prose cells wrap, at a
 *     readable measure.
 *   - Numeric columns are detected from the rendered DOM and get right
 *     alignment + tabular figures. Explicit GFM alignment still wins (it
 *     arrives as an inline style, which beats our class).
 *   - Sticky header, and a pinned first column once the table actually scrolls,
 *     so the row label stays visible.
 *   - Copy/export control (markdown, CSV, XLSX) on hover.
 *
 * Layout facts worth knowing before editing:
 *   - Sticky cells are marked with `data-pin` / styled through table-level
 *     arbitrary variants rather than per-cell props, because react-markdown
 *     gives `th`/`td` no column index - the effect below reads the rendered DOM
 *     instead.
 *   - Separators on sticky cells are inset box-shadows, not borders: Chromium
 *     scrolls a collapsed border away from a sticky cell.
 *   - When the first column is pinned the left edge fade is dropped, otherwise
 *     the mask would fade out the pinned column itself.
 */

import * as React from 'react'
import { cn } from '../../lib/utils'
import { useScrollFade } from './useScrollFade'
import { TableExportDropdown } from './TableExportDropdown'
import type { ExportColumn } from './table-export'

/** Row count above which the table gets its own vertical scroll area. */
const STICKY_SCROLL_ROW_THRESHOLD = 12

/** Cell text length up to which a cell is kept on a single line. */
const NOWRAP_MAX_CHARS = 32

/**
 * Widest the first column may be (as a fraction of the visible width) and still
 * be worth pinning. Pinning a wide prose column would eat the viewport.
 */
const MAX_PIN_WIDTH_RATIO = 0.4

/** Width of the right-edge scroll fade. */
const FADE_SIZE = 32

/**
 * Numbers, money and percentages in both en-US and vi-VN formatting
 * (1,234.5 / 1.234,5 / 12,5% / 1.200.000d / -3.2 / (1.234) for negatives).
 * Deliberately does NOT match dates (2026-08-21, 21/08/2026) - those read
 * better left-aligned.
 */
const NUMERIC_RE = /^[([]?[+-]?\s*[$€£¥₫]?\s?\d[\d\s.,]*\s*(?:%|[a-zA-Z]{1,3}|[$€£¥₫])?\s*[)\]]?$/

function looksNumeric(text: string): boolean {
  if (!/\d/.test(text)) return false
  // A trailing unit is fine (10 kg, 5%, 1.2M) but a sentence containing a
  // number is not - cap the non-numeric tail.
  if (text.replace(/[\d\s.,()[\]+\-$€£¥₫%]/g, '').length > 3) return false
  return NUMERIC_RE.test(text)
}

/** Cell text treated as "no value" when deciding whether a column is numeric. */
function isBlankCell(text: string): boolean {
  return text === '' || text === '-' || text === '--' || text === '—'
}

/**
 * Parse a displayed value into a real number for the XLSX export, or null when
 * the format is ambiguous.
 *
 * Conservative by design: a single dot or comma followed by exactly three
 * digits ("1.234") means 1234 in vi-VN and 1.234 in en-US, and there is no way
 * to tell from one cell. Those stay text rather than silently exporting a
 * number that is off by 1000. Percentages stay text too - writing 12.5 into a
 * percent-formatted cell would render as 1250%.
 */
export function parseDisplayNumber(text: string): number | null {
  const raw = text.trim()
  if (!raw || raw.includes('%')) return null
  // A date, range or ratio ("2026-08-21", "21/08/2026", "3:2") is not a number,
  // and stripping its separators would fabricate one (20260821).
  if (/[/:]/.test(raw)) return null

  const unwrapped = raw.replace(/^[([]/, '').replace(/[)\]]$/, '')
  if (unwrapped.slice(1).includes('-')) return null

  const negative = unwrapped.startsWith('-') || /^\(.*\)$/.test(raw)
  let body = unwrapped
    .replace(/[$€£¥₫\s+-]/g, '')
    .replace(/[a-zA-Z]+$/, '')
  if (!body || !/^[\d.,]+$/.test(body)) return null

  const dots = (body.match(/\./g) ?? []).length
  const commas = (body.match(/,/g) ?? []).length

  if (dots > 0 && commas > 0) {
    // Mixed separators: whichever comes last is the decimal separator.
    const decimalSep = body.lastIndexOf('.') > body.lastIndexOf(',') ? '.' : ','
    const groupSep = decimalSep === '.' ? ',' : '.'
    body = body.split(groupSep).join('').replace(decimalSep, '.')
  } else if (dots > 0 || commas > 0) {
    const sep = dots > 0 ? '.' : ','
    const parts = body.split(sep)
    const head = parts[0] ?? ''
    const tail = parts.slice(1)
    if (tail.length === 1 && tail[0]?.length === 3 && head.length <= 3) {
      return null // ambiguous: 1.234 could be 1234 or 1.234
    }
    if (tail.every((p) => p.length === 3) && (tail.length > 1 || head.length > 3)) {
      body = parts.join('') // thousands grouping
    } else if (tail.length === 1) {
      body = `${head}.${tail[0]}` // decimal separator
    } else {
      return null
    }
  }

  const value = Number(body)
  if (!isFinite(value)) return null
  return negative ? -value : value
}

/** Flatten React children to plain text, so a cell can size itself. */
function plainText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(plainText).join('')
  if (React.isValidElement(node)) {
    return plainText((node.props as { children?: React.ReactNode }).children)
  }
  return ''
}

/** Windows-safe download filename derived from the nearest preceding heading. */
function nearestHeading(el: HTMLElement | null): string {
  let node = el?.previousElementSibling ?? null
  while (node) {
    if (/^H[1-6]$/.test(node.tagName)) {
      const text = (node.textContent ?? '').trim().replace(/[\\/:*?"<>|]/g, '-')
      if (text) return text.slice(0, 80)
    }
    node = node.previousElementSibling
  }
  return 'table'
}

export type MarkdownTableDensity = 'compact' | 'comfortable'

interface TableStyle {
  density: MarkdownTableDensity
  /** Monospace cells (terminal mode). */
  mono: boolean
}

const TableStyleContext = React.createContext<TableStyle>({ density: 'compact', mono: false })

const CELL_PADDING: Record<MarkdownTableDensity, string> = {
  compact: 'py-2 px-3',
  comfortable: 'py-2.5 px-4',
}

interface ExportPayload {
  columns: ExportColumn[]
  rows: Record<string, string>[]
  xlsxColumns: ExportColumn[]
  xlsxRows: Record<string, string | number>[]
  filename: string
}

interface TableLayout {
  /** Table is taller than the row threshold: give it its own scroll area. */
  tall: boolean
  /** First column is pinned (table overflows and that column is narrow enough). */
  pinned: boolean
}

export interface MarkdownTableProps {
  children?: React.ReactNode
  density?: MarkdownTableDensity
  mono?: boolean
  className?: string
}

export function MarkdownTable({
  children,
  density = 'compact',
  mono = false,
  className,
}: MarkdownTableProps) {
  const tableRef = React.useRef<HTMLTableElement>(null)
  const { scrollRef, maskImage, canScrollRight } = useScrollFade(FADE_SIZE)
  const [layout, setLayout] = React.useState<TableLayout>({ tall: false, pinned: false })
  const [exportPayload, setExportPayload] = React.useState<ExportPayload | null>(null)
  // Signature of the last payload pushed to state, so a re-scan during
  // streaming only re-renders when the table content actually changed.
  const payloadSignature = React.useRef('')

  // Numeric-column detection, pin decision and export payload, recomputed
  // whenever the table content or its available width changes (markdown streams
  // in a cell at a time). Alignment/pin markers are set directly on the DOM:
  // React never wrote those attributes, so it leaves them alone, and if it does
  // recreate a cell the observer fires again.
  React.useEffect(() => {
    const table = tableRef.current
    const scroller = scrollRef.current
    if (!table || !scroller) return

    let frame = 0
    const scan = () => {
      frame = 0
      const headerCells = Array.from(table.tHead?.rows[0]?.cells ?? [])
      const bodyRows = Array.from(table.tBodies[0]?.rows ?? [])

      const colCount = bodyRows.reduce(
        (max, row) => Math.max(max, row.cells.length),
        headerCells.length,
      )

      // ── numeric columns ────────────────────────────────────────────────
      const numericColumns: boolean[] = []
      const columnParses: boolean[] = []
      for (let col = 0; col < colCount; col++) {
        let filled = 0
        let numeric = 0
        let parsable = 0
        for (const row of bodyRows) {
          const cell = row.cells[col]
          if (!cell || cell.colSpan > 1) continue
          const text = cell.textContent?.trim() ?? ''
          if (isBlankCell(text)) continue
          filled++
          if (looksNumeric(text)) numeric++
          if (parseDisplayNumber(text) !== null) parsable++
        }
        numericColumns[col] = filled > 0 && numeric === filled
        columnParses[col] = filled > 0 && parsable === filled
      }

      // ── pin decision ──────────────────────────────────────────────────
      // Only pin when the table actually scrolls sideways and the first column
      // is narrow enough to be worth the space it takes from the viewport.
      const visibleWidth = scroller.clientWidth
      const overflows = table.scrollWidth > visibleWidth + 1
      const firstColWidth =
        headerCells[0]?.getBoundingClientRect().width ??
        bodyRows[0]?.cells[0]?.getBoundingClientRect().width ??
        0
      const pinned =
        overflows &&
        colCount > 1 &&
        firstColWidth > 0 &&
        firstColWidth <= visibleWidth * MAX_PIN_WIDTH_RATIO

      for (const row of Array.from(table.rows)) {
        for (let col = 0; col < row.cells.length; col++) {
          const cell = row.cells[col]
          if (!cell || cell.colSpan > 1) continue
          cell.toggleAttribute('data-numeric', numericColumns[col] === true)
          cell.toggleAttribute('data-pin', pinned && col === 0)
        }
      }

      setLayout((prev) => {
        const next = { tall: bodyRows.length > STICKY_SCROLL_ROW_THRESHOLD, pinned }
        return prev.tall === next.tall && prev.pinned === next.pinned ? prev : next
      })

      // ── export payload ────────────────────────────────────────────────
      if (bodyRows.length === 0 || colCount === 0) {
        if (payloadSignature.current !== '') {
          payloadSignature.current = ''
          setExportPayload(null)
        }
        return
      }

      const columns: ExportColumn[] = []
      for (let col = 0; col < colCount; col++) {
        const label = headerCells[col]?.textContent?.trim() || `Column ${col + 1}`
        // Key by index, not label: markdown tables may repeat a header name.
        columns.push({ key: `c${col}`, label })
      }
      const xlsxColumns: ExportColumn[] = columns.map((col, i) => ({
        ...col,
        // Only claim a numeric column when every value actually parsed;
        // otherwise Excel gets the display text unchanged.
        type: numericColumns[i] && columnParses[i] ? 'number' : undefined,
      }))

      const rows: Record<string, string>[] = []
      const xlsxRows: Record<string, string | number>[] = []
      for (const row of bodyRows) {
        const display: Record<string, string> = {}
        const parsed: Record<string, string | number> = {}
        for (let col = 0; col < colCount; col++) {
          const text = row.cells[col]?.textContent?.trim() ?? ''
          const key = `c${col}`
          display[key] = text
          const asNumber =
            xlsxColumns[col]?.type === 'number' ? parseDisplayNumber(text) : null
          parsed[key] = asNumber ?? text
        }
        rows.push(display)
        xlsxRows.push(parsed)
      }

      const filename = nearestHeading(scroller.parentElement)
      const signature = JSON.stringify([columns, rows, filename])
      if (signature !== payloadSignature.current) {
        payloadSignature.current = signature
        setExportPayload({ columns, rows, xlsxColumns, xlsxRows, filename })
      }
    }

    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(scan)
    }

    scan()
    const observer = new MutationObserver(schedule)
    observer.observe(table, { childList: true, subtree: true, characterData: true })
    // Width changes flip the pin decision without touching the DOM content.
    const resize = new ResizeObserver(schedule)
    resize.observe(scroller)
    return () => {
      observer.disconnect()
      resize.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [children, scrollRef])

  // With a pinned column the left-edge fade would fade the pinned column
  // itself, so only the right edge fades once the first column is frozen.
  const mask = layout.pinned
    ? canScrollRight
      ? `linear-gradient(to right, black calc(100% - ${FADE_SIZE}px), transparent)`
      : undefined
    : maskImage

  return (
    <TableStyleContext.Provider value={{ density, mono }}>
      {/* min-w-0 keeps the wrapper shrinkable inside flex parents (chat
          bubbles, popovers) instead of stretching them to the table width. */}
      <div className={cn('group/table relative my-3 min-w-0 max-w-full', className)}>
        <div
          ref={scrollRef}
          className={cn(
            'min-w-0 max-w-full overflow-x-auto overflow-y-auto rounded-[8px] shadow-thin',
            layout.tall && 'max-h-[420px]',
          )}
          style={{ maskImage: mask, WebkitMaskImage: mask }}
        >
          <table
            ref={tableRef}
            className={cn(
              // w-max: grow to content and let the wrapper scroll, rather than
              // squeezing every column into the container width.
              'w-max min-w-full border-collapse',
              mono ? 'font-mono text-[12.5px]' : density === 'comfortable' ? 'text-sm' : 'text-[13px]',
              '[&_[data-numeric]]:text-right [&_[data-numeric]]:tabular-nums',
              // Pinned first column. Opaque background so the scrolling cells
              // pass underneath it; the header cell also keeps its own bottom
              // separator, hence the both-edges token.
              '[&_[data-pin]]:sticky [&_[data-pin]]:left-0',
              '[&_td[data-pin]]:z-[1] [&_td[data-pin]]:bg-background',
              '[&_td[data-pin]]:shadow-right-border-thin',
              '[&_th[data-pin]]:z-[3] [&_th[data-pin]]:bg-background',
              '[&_th[data-pin]]:shadow-right-bottom-border-thin',
            )}
          >
            {children}
          </table>
        </div>

        {/* Copy/export control - revealed on hover, like the datatable block's.
            has-[[data-state=open]] keeps the trigger visible while its menu is
            open: Radix portals the menu outside this wrapper, so the pointer
            moving onto it drops `group-hover` and would otherwise fade the
            trigger out from under the user. */}
        {exportPayload && (
          <div className="absolute top-1.5 right-1.5 opacity-0 transition-opacity group-hover/table:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100">
            <TableExportDropdown
              columns={exportPayload.columns}
              rows={exportPayload.rows}
              filename={exportPayload.filename}
              xlsxColumns={exportPayload.xlsxColumns}
              xlsxRows={exportPayload.xlsxRows}
            />
          </div>
        )}
      </div>
    </TableStyleContext.Provider>
  )
}

export function MarkdownTableHead({ children }: { children?: React.ReactNode }) {
  return <thead className="sticky top-0 z-[2] bg-background">{children}</thead>
}

export function MarkdownTableBody({ children }: { children?: React.ReactNode }) {
  return <tbody>{children}</tbody>
}

export function MarkdownTableRow({ children }: { children?: React.ReactNode }) {
  return (
    <tr className="border-b border-foreground/[0.03] last:border-0 hover:bg-foreground/[0.015] transition-colors">
      {children}
    </tr>
  )
}

/**
 * Header cell. Kept on one line and painted opaque so it stays readable while
 * the body scrolls under it. The separator is an inset shadow rather than a
 * border because borders on a sticky `th` scroll away in Chromium.
 */
export function MarkdownTableHeaderCell({
  children,
  style,
}: {
  children?: React.ReactNode
  style?: React.CSSProperties
}) {
  const { density } = React.useContext(TableStyleContext)
  return (
    <th
      style={style}
      className={cn(
        CELL_PADDING[density],
        'text-left align-bottom whitespace-nowrap',
        'text-[12px] font-medium text-muted-foreground',
        'bg-foreground/[0.02] shadow-bottom-border-thin',
      )}
    >
      {children}
    </th>
  )
}

/**
 * Body cell. Short values stay on one line so numbers and IDs never break
 * mid-token; long prose wraps at a readable measure instead of forcing an
 * absurdly wide table.
 */
export function MarkdownTableCell({
  children,
  style,
}: {
  children?: React.ReactNode
  style?: React.CSSProperties
}) {
  const { density } = React.useContext(TableStyleContext)
  const long = plainText(children).trim().length > NOWRAP_MAX_CHARS
  return (
    <td
      style={style}
      className={cn(
        CELL_PADDING[density],
        long ? 'align-top min-w-[24ch] max-w-[52ch]' : 'align-middle whitespace-nowrap',
      )}
    >
      {children}
    </td>
  )
}
